import { createAsyncThunk } from '@reduxjs/toolkit';
import focusBearApi from 'services/axios-config';
import Endpoints from 'constants/endpoints';
import { RootState } from 'store';
import { HTTP_STATS_CODE, PAGINATE_ITEMS } from 'constants/general';
import {
  resetPagination,
  updateOrderBy,
  updateIsFetchingActivities,
  updateIsFetchingFocusModes,
  updateIsFetchingUserDetails,
  updateIsLoading,
  updateIsFetchingSurveys,
  updateIsUpdatingSurvey,
  updateIsCreatingSurvey,
  updateIsFetchingTasks,
  updateIsExtendingTrial,
  updateIsCancelingSubscription,
  updateIsUpgradingPlan,
  updateIsFetchingAnnouncements,
  updateIsCreatingAnnouncement,
  updateIsUpdatingAnnouncement,
  updateIsDeletingAnnouncement
} from './slice';
import {
  CreateAnnouncementPayload,
  GetAnnouncementsParams,
  UpdateAnnouncementPayload
} from 'interfaces/admin.interface';
import {
  CreateSurveyPayload,
  GetActivitiesByUserIdOrStripeIdOrActivityTypePayload,
  UpdateSurveyPayload
} from 'interfaces';
import { AxiosError } from 'axios';
import { t } from 'services/i18n.service';
import isUUID from 'validator/es/lib/isUUID';
import { FetchUsersOrderByOptions } from 'enums';
import {
  updatePaginationNavigation,
  updatePaginationCurrentPage,
  updatePaginationTotalPages
} from '../pagination/slice';
import { decrement } from 'utils/helpers';
import { captureException } from 'services/posthog.service';
import { toast } from 'react-toastify';

export const getUsers = createAsyncThunk(
  'admin/get_user_list',
  async (
    {
      orderBy,
      shouldAppend = false
    }: { orderBy: string; shouldAppend?: boolean },
    { dispatch, getState }
  ) => {
    try {
      const take = `${PAGINATE_ITEMS}`;
      const {
        admin: { skip }
      } = getState() as RootState;
      dispatch(updateIsLoading(true));
      const params = orderBy
        ? {
            take,
            skip,
            orderBy
          }
        : {
            take,
            skip
          };
      const { data } = await focusBearApi.get(Endpoints.USER_LIST, {
        params
      });
      return { shouldAppend, data };
    } catch (error) {
      dispatch(updateIsLoading(false));
      captureException(JSON.stringify(error));
    }
  }
);

export const getUserInfoAndFocusModes = createAsyncThunk(
  'admin/get_user_info_and_focus_modes',
  async (user_id: string, { dispatch }) => {
    try {
      dispatch(updateIsFetchingFocusModes(true));
      const { data } = await focusBearApi.get(
        Endpoints.USER_INFO_AND_FOCUS_MODES,
        {
          params: { id: user_id }
        }
      );
      return data;
    } catch (error) {
      dispatch(updateIsFetchingFocusModes(false));
      captureException(JSON.stringify(error));
    }
  }
);

export const getActivitiesByUserIdOrStripeIdOrActivityType = createAsyncThunk(
  'admin/get_activities_by_userId_or_stripeId_or_ActivityType',
  async (
    {
      user_id,
      stripe_customer_id,
      page_num = PAGINATE_ITEMS
    }: GetActivitiesByUserIdOrStripeIdOrActivityTypePayload,
    { dispatch }
  ) => {
    try {
      dispatch(updateIsFetchingActivities(true));
      let params: GetActivitiesByUserIdOrStripeIdOrActivityTypePayload = {
        page_num
      };
      if (user_id && isUUID(user_id)) {
        params = { ...params, user_id };
      }
      if (stripe_customer_id) {
        params = { ...params, stripe_customer_id };
      }
      const { data } = await focusBearApi.get(Endpoints.ACTIVITY, {
        params
      });
      return data ?? [];
    } catch (error) {
      dispatch(updateIsFetchingActivities(false));
      captureException(JSON.stringify(error));
    }
  }
);

export const getUserByIdOrStripeIdOrEmail = createAsyncThunk(
  'admin/get_user_by_id_or_stripe_id_or_email',
  async (
    params: { user_id?: string; stripe_customer_id?: string; email?: string },
    { dispatch }
  ) => {
    try {
      dispatch(updateIsFetchingUserDetails(true));
      dispatch(updateOrderBy(FetchUsersOrderByOptions.NONE));
      dispatch(resetPagination());
      const { data } = await focusBearApi.get(Endpoints.USER, {
        params
      });
      return data ? [data] : [];
    } catch (error) {
      captureException(JSON.stringify(error));
    }
  }
);

export const getUserDevices = createAsyncThunk(
  'admin/get_user_devices',
  async (user_id: string) => {
    try {
      const { data } = await focusBearApi.get(Endpoints.ADMIN_USER_DEVICE, {
        params: {
          user_id
        }
      });
      return data ?? [];
    } catch (error) {
      captureException(JSON.stringify(error));
    }
  }
);

export const getTasksByUserId = createAsyncThunk(
  'admin/get_tasks_by_user_id',
  async (user_id: string, { dispatch }) => {
    try {
      dispatch(updateIsFetchingTasks(true));
      const { data } = await focusBearApi.get(Endpoints.ADMIN_USER_TASKS, {
        params: {
          user_id
        }
      });
      return data ?? [];
    } catch (error) {
      dispatch(updateIsFetchingTasks(false));
      captureException(JSON.stringify(error));
    }
  }
);

export const createSurvey = createAsyncThunk(
  'admin/create_survey',
  async (payload: CreateSurveyPayload, { dispatch, getState }) => {
    try {
      const { currentPage, itemsPerPage } = (getState() as RootState)
        .pagination;
      dispatch(updateIsCreatingSurvey(true));
      const { data, status } = await focusBearApi.post(
        Endpoints.SURVEY,
        payload
      );
      status === HTTP_STATS_CODE.CREATED &&
        dispatch(getSurveys({ page: currentPage, take: itemsPerPage }));
      return data ?? null;
    } catch {
      dispatch(updateIsCreatingSurvey(false));
      captureException(JSON.stringify);
    }
  }
);

export const getSurveys = createAsyncThunk(
  'admin/get_surveys',
  async (
    payload: {
      page: number;
      order?: string;
      take?: number;
    },
    { dispatch, getState }
  ) => {
    try {
      dispatch(updateIsFetchingSurveys(true));
      const { data } = await focusBearApi.get(Endpoints.SURVEY, {
        params: {
          ...payload
        }
      });
      dispatch(
        updatePaginationNavigation({
          hasNextPage: data?.meta?.hasNextPage,
          hasPreviousPage: data?.meta?.hasPreviousPage
        })
      );
      if (!data?.data?.length) {
        const { currentPage, totalPages } = (getState() as RootState)
          .pagination;
        dispatch(updatePaginationCurrentPage(decrement(currentPage)));
        dispatch(updatePaginationTotalPages(decrement(totalPages)));
      }
      return data?.data ?? [];
    } catch {
      dispatch(updateIsFetchingSurveys(false));
      captureException(JSON.stringify);
    }
  }
);

export const updateSurvey = createAsyncThunk(
  'admin/update_survey',
  async (payload: UpdateSurveyPayload, { dispatch, getState }) => {
    try {
      const { currentPage, itemsPerPage } = (getState() as RootState)
        .pagination;
      dispatch(updateIsUpdatingSurvey(true));
      const { status } = await focusBearApi.patch(Endpoints.SURVEY, payload);
      status === HTTP_STATS_CODE.SUCCESS &&
        dispatch(getSurveys({ page: currentPage, take: itemsPerPage }));
      dispatch(updateIsUpdatingSurvey(false));
    } catch {
      dispatch(updateIsUpdatingSurvey(false));
      captureException(JSON.stringify);
    }
  }
);

export const extendUserTrial = createAsyncThunk(
  'admin/extend_user_trial',
  async (
    { user_id, days }: { user_id: string; days: number },
    { dispatch }
  ) => {
    try {
      dispatch(updateIsExtendingTrial(true));
      const { status } = await focusBearApi.post(Endpoints.ADMIN_EXTEND_TRIAL, {
        user_id,
        days
      });
      if (
        status === HTTP_STATS_CODE.SUCCESS ||
        status === HTTP_STATS_CODE.CREATED
      ) {
        toast.success(`Trial extended by ${days} days`);
      } else {
        toast.warning('Extend trial request returned unexpected status');
      }
    } catch (error) {
      toast.error('Failed to extend trial');
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsExtendingTrial(false));
    }
  }
);

export const cancelUserSubscription = createAsyncThunk(
  'admin/cancel_user_subscription',
  async (
    { user_id, reason }: { user_id: string; reason?: string },
    { dispatch }
  ) => {
    try {
      dispatch(updateIsCancelingSubscription(true));
      const { status } = await focusBearApi.post(
        Endpoints.ADMIN_CANCEL_SUBSCRIPTION,
        {
          user_id,
          reason
        }
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        toast.success('Subscription cancelled');
      } else {
        toast.warning('Cancel subscription returned unexpected status');
      }
    } catch (error) {
      toast.error('Failed to cancel subscription');
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsCancelingSubscription(false));
    }
  }
);

// Pull a human-readable message out of a NestJS-style error response.
// `message` can be a string or an array of validation strings.
// Shared with the admin-teams thunks (store/reducer/admin-teams/extra.ts).
export const getApiErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  const data = (error as AxiosError<{ message?: string | string[] }>)?.response
    ?.data;
  const message = data?.message;
  if (Array.isArray(message)) {
    return message.join(', ');
  }
  return message || fallback;
};

export const getAnnouncements = createAsyncThunk(
  'admin/get_announcements',
  async (params: GetAnnouncementsParams, { dispatch }) => {
    try {
      dispatch(updateIsFetchingAnnouncements(true));
      // Admin list route (all announcements incl. expired) — distinct from the
      // user-facing GET /announcements feed. Added in backend#2107.
      const { data } = await focusBearApi.get(Endpoints.ANNOUNCEMENTS_ADMIN, {
        params
      });
      return data ?? { data: [], meta: null };
    } catch (error) {
      dispatch(updateIsFetchingAnnouncements(false));
      // List fetch failures are logged, not toasted (route may be unavailable
      // until backend#2107 ships).
      captureException(error);
    }
  }
);

export const createAnnouncement = createAsyncThunk(
  'admin/create_announcement',
  async (payload: CreateAnnouncementPayload, { dispatch }) => {
    try {
      dispatch(updateIsCreatingAnnouncement(true));
      const { data } = await focusBearApi.post(
        Endpoints.ANNOUNCEMENTS,
        payload
      );
      toast.success(t('admin.announcement_created'));
      return data ?? null;
    } catch (error) {
      dispatch(updateIsCreatingAnnouncement(false));
      // Backend message surfaces 409 (duplicate id) / 400 (validation) verbatim.
      toast.error(
        getApiErrorMessage(error, t('admin.announcement_create_failed'))
      );
      captureException(error);
      throw error;
    }
  }
);

export const updateAnnouncement = createAsyncThunk(
  'admin/update_announcement',
  async (payload: UpdateAnnouncementPayload, { dispatch }) => {
    try {
      dispatch(updateIsUpdatingAnnouncement(true));
      const { id, ...body } = payload;
      const { data } = await focusBearApi.put(
        Endpoints.ANNOUNCEMENT_BY_ID(id),
        body
      );
      toast.success(t('admin.announcement_updated'));
      return data ?? null;
    } catch (error) {
      dispatch(updateIsUpdatingAnnouncement(false));
      toast.error(
        getApiErrorMessage(error, t('admin.announcement_update_failed'))
      );
      captureException(error);
      throw error;
    }
  }
);

export const deleteAnnouncement = createAsyncThunk(
  'admin/delete_announcement',
  async (id: string, { dispatch }) => {
    try {
      dispatch(updateIsDeletingAnnouncement(true));
      await focusBearApi.delete(Endpoints.ANNOUNCEMENT_BY_ID(id));
      toast.success(t('admin.announcement_deleted'));
      return id;
    } catch (error) {
      dispatch(updateIsDeletingAnnouncement(false));
      toast.error(
        getApiErrorMessage(error, t('admin.announcement_delete_failed'))
      );
      captureException(error);
      throw error;
    }
  }
);

export const upgradeUserSubscription = createAsyncThunk(
  'admin/upgrade_user_subscription',
  async (
    { user_id, plan_id }: { user_id: string; plan_id: string },
    { dispatch }
  ) => {
    try {
      dispatch(updateIsUpgradingPlan(true));
      const { status } = await focusBearApi.post(
        Endpoints.ADMIN_UPGRADE_SUBSCRIPTION,
        {
          user_id,
          plan_id
        }
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        toast.success('Subscription upgraded');
      } else {
        toast.warning('Upgrade subscription returned unexpected status');
      }
    } catch (error) {
      toast.error('Failed to upgrade subscription');
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsUpgradingPlan(false));
    }
  }
);
